import { EVENTS } from '@archetype-themes/utils/events'

class GiftWithPurchase extends HTMLElement {
  constructor() {
    super()
    this.rules = []
    this.initializeRules()
    this.attachEventListeners()
  }

  initializeRules() {
    if (!window.theme || !window.theme.giftWithPurchaseRules) return

    const ruleConfigs = window.theme.giftWithPurchaseRules

    this.rules = ruleConfigs.map(config => {
      if (config.type === 'collection_promo') {
        return new CollectionGWPRule(config)
      } else if (config.type === 'price_promo') {
        return new PriceGWPRule(config)
      }
      return null
    }).filter(rule => rule !== null)
  }

  attachEventListeners() {
    // Listen for general cart updates
    document.addEventListener(EVENTS.cartUpdated, this.handleCartUpdate.bind(this))

    // Listen for specific product additions
    document.addEventListener(EVENTS.ajaxProductAdded, this.handleProductAdded.bind(this))
  }

  async handleProductAdded(event) {
    try {
      // Extract product information from the event detail
      const { product } = event.detail

      // Get the current cart (we still need to fetch it for the complete state)
      const cart = await this.fetchCart()

      // Process GWP rules with the newly added product information
      await this.processGiftWithPurchase(cart, product)
    } catch (error) {
      console.error('Error processing GWP rules after product added:', error)
    }
  }

  async handleCartUpdate() {
    try {
      const cart = await this.fetchCart()
      await this.processGiftWithPurchase(cart)
    } catch (error) {
      console.error('Error processing GWP rules:', error)
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js')
    return await response.json()
  }

  async processGiftWithPurchase(cart, newProduct = null) {
    // Get eligible gifts with quantities the cart is eligible for {id, quantity = 0}
    const giftLineItems = await this.getGiftLineItems(cart)

    // Prepare cart updates
    const updates = {}

    giftLineItems.forEach(item => {
      if (item.quantity === 0 && cart.items.map(item => item.variant_id).indexOf(item.id) === -1) return

      let quantityChanged = true
      cart.items.forEach(cart_item => {
        if (cart_item.variant_id === item.id && cart_item.quantity === item.quantity) {
          quantityChanged = false
          return
        }
      })

      if (!quantityChanged) return

      updates[item.id] = item.quantity
    })

    if (Object.keys(updates).length === 0) return

    const data = {
      updates,
      sections: ['cart-ajax']
    }

    // Update the cart in a single request
    await this.updateCart(data)
  }

  async getEligibleGiftIds(cart) {
    const eligibleRules = []
    for (const rule of this.rules) {
      if (await rule.isEligible(cart)) {
        eligibleRules.push(rule)
      }
    }
    return eligibleRules.map(rule => rule.giftVariantId)
  }

  async getGiftLineItems(cart) {
    const giftLineItems = []
    for (const rule of this.rules) {
      const cartItem = await rule.cartItem(cart)
      if (cartItem) {
        giftLineItems.push(cartItem)
      }
    }

    return giftLineItems
  }

  getGiftVariantIdsInCart(cart) {
    const allGiftIds = this.rules.map(rule => rule.giftVariantId)

    return cart.items
      .filter(item => allGiftIds.includes(item.variant_id))
      .map(item => item.variant_id)
  }

  async updateCart(data) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 422 || data.status === 'bad_request') {
            if (data.description) {
              alert(data.description)
            }
          } else {
            this.dispatchEvent(new CustomEvent(EVENTS.ajaxProductAdded, {
              bubbles: true,
              detail: {
                product: data,
              }
            }))
          }
        })
    } catch (error) {
      console.error('Error updating cart with GWP items:', error)
      throw error
    }
  }
}

class GWPRule {
  constructor(config) {
    this.giftVariantId = config.gift_variant_id
  }

  async isEligible(cart) {
    // To be implemented by subclasses
    return false
  }

  async cartItem(cart) {
    return { id: this.giftVariantId, quantity: 0 }
  }

  // Helper method to determine if an item belongs to a collection
  async getItemCollections(item) {
    if (!item.handle) {
      return []
    }

    try {
      // Fetch the product-collections section using the Section Rendering API
      const response = await fetch(`/products/${item.handle}?sections=product-collections`)
      if (!response.ok) {
        console.error('Failed to fetch product collections')
        return []
      }

      const data = await response.json()

      // The response will have a key like "product-collections" with the rendered HTML
      const sectionHtml = data['product-collections']

      if (!sectionHtml) {
        return []
      }

      // Create a DOM parser to extract the JSON from the HTML
      const parser = new DOMParser()
      const doc = parser.parseFromString(sectionHtml, 'text/html')

      // Find the script tag containing the JSON
      const scriptTag = doc.querySelector('script[type="application/json"]')

      if (!scriptTag || !scriptTag.textContent) {
        return []
      }

      // Parse the JSON content
      const collectionsData = JSON.parse(scriptTag.textContent)

      // Return the collections array or an empty array if it doesn't exist
      return collectionsData.collections || []
    } catch (error) {
      console.error('Error fetching product collections:', error)
      return []
    }
  }
}

class CollectionGWPRule extends GWPRule {
  constructor(config) {
    super(config)
    this.collections = config.collections || []
  }

  async isEligible(cart) {
    if (!this.collections.length) return false

    // Check if any product in the cart belongs to one of the rule's collections
    for (const item of cart.items) {
      const itemCollections = await this.getItemCollections(item)
      if (itemCollections.some(collectionId => this.collections.includes(collectionId))) {
        return true
      }
    }
    return false
  }

  async cartItem(cart) {
    if (!this.collections.length) return { id: this.giftVariantId, quantity: 0 }

    let quantity = 0

    for (const item of cart.items) {
      const itemCollections = await this.getItemCollections(item)
      if (itemCollections.some(collectionId => this.collections.includes(collectionId))) {
        quantity += item.quantity
      }
    }

    return { id: this.giftVariantId, quantity }
  }
}

class PriceGWPRule extends GWPRule {
  constructor(config) {
    super(config)
    this.excludeCollections = config.exclude_collections || []
    this.minimumSpend = config.minimmum_spend !== null ? config.minimmum_spend : 0
  }

  async eligibleQuantity(cart) {
    if (cart.total_price < this.minimumSpend) return 0

    // Calculate total cart value excluding items from excluded collections
    let eligibleTotal = cart.total_price

    for (const item of cart.items) {
      // avoid recursive gift eligibility if non-free gift item is added
      if (item.variant_id === this.giftVariantId) {
        eligibleTotal -= item.line_price
        continue
      }

      if (this.excludeCollections.length > 0) {
        const itemCollections = await this.getItemCollections(item)

        // If this item belongs to any excluded collection, subtract its price
        if (itemCollections.some(collectionId =>
          this.excludeCollections.includes(collectionId)
        )) {
          eligibleTotal -= item.line_price
        }
      }
    }

    // Shopify typically returns prices in cents, convert to dollars for comparison
    const eligibleTotalDollars = eligibleTotal / 100

    // Return quantity of gift items the cart is eligible for
    return Math.floor(eligibleTotalDollars / this.minimumSpend)
  }

  async cartItem(cart) {
    const quantity = await this.eligibleQuantity(cart)

    return { id: this.giftVariantId, quantity }
  }
}


if (!customElements.get('gift-with-purchase')) {
  customElements.define('gift-with-purchase', GiftWithPurchase)
}

